import { useCallback, useEffect, useRef } from 'react';
import { useReactFlow } from '@xyflow/react';
import type { Connection } from '@xyflow/react';

import { type WorkflowMutationEntry } from '@/components/chat';
import { useNotification } from '@/components/notifications';
import {
  useNodeStore,
  beginSnapshotSuppression,
  endSnapshotSuppression,
} from '@/stores/node-store';
import {
  type NodeStruct,
  type NodeParam,
  type InputValue,
} from '@/types/node-types';
import { type CustomEdge } from '@/types/edge-types';
import {
  type ChatApplySaveReport,
  requestChatApplySave,
  useChatApplyStore,
} from '@/stores/chat-apply-store';
import { findCatalogStruct } from '@/stores/helpers/node-catalog-lookup';
import { coerceChatValueToParamType } from '@/stores/helpers/chat-field-coercion';
import { getAutoLayoutPositions } from '@/pages/workflow/helper/auto-layout';

export function getEditingLockedWarning(isWorkflowLockActive: boolean): string {
  return isWorkflowLockActive
    ? 'Workflow is locked by an admin — ask them to unlock it to apply chat-proposed changes.'
    : 'Canvas editing is locked — unlock it to apply chat-proposed changes.';
}

/**
 * Applies agent-proposed workflow mutations onto the canvas: enforces the
 * editing lock, groups a batch (+ auto-layout) into one undo step, and
 * reports success/partial-failure. Shared by the legacy per-node chat card,
 * the A2A canvas chat card, and the embedded chat rail's Apply card so every
 * apply path behaves alike -- this is canvas/node-store logic, not chat
 * state, so it does not need a chat connection mounted to run.
 */
export function useWorkflowMutationApplier(options: {
  isEditingLocked: boolean;
  isWorkflowLockActive: boolean;
}) {
  const notification = useNotification();
  const { fitView } = useReactFlow();
  const {
    handleParameterChange,
    updateNodeAttributes,
    addNode: storeAddNode,
    onNodesEdgesDelete,
    onEdgesDelete,
    onConnect,
    saveSnapshot,
  } = useNodeStore();

  const isEditingLockedRef = useRef(options.isEditingLocked);
  isEditingLockedRef.current = options.isEditingLocked;
  const isWorkflowLockActiveRef = useRef(options.isWorkflowLockActive);
  isWorkflowLockActiveRef.current = options.isWorkflowLockActive;

  // The delayed auto-layout below reads the (global) node store well after
  // this call returns -- if the canvas has since switched workflows or
  // unmounted, that store now holds a different graph, and applying layout
  // to it would silently mutate the wrong workflow.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reads the store rather than getNode(): an add_node → configure_node batch
  // applies in one synchronous loop, where getNode() can't see the new node
  // yet and the value would silently skip coercion.
  const coerceChatFieldValue = useCallback(
    (nodeId: string, fieldId: string, value: unknown): InputValue => {
      const node = useNodeStore.getState().nodes.find((n) => n.id === nodeId);
      const params = (
        node?.data as { struct?: { params?: NodeParam[] } } | undefined
      )?.struct?.params;
      const param = params?.find((p) => p.field_id === fieldId);
      return coerceChatValueToParamType(param, value);
    },
    [],
  );

  // Execute a single workflow mutation event on the canvas.
  const executeWorkflowMutation = useCallback(
    (data: Record<string, unknown>) => {
      const eventType = data.event_type as string;

      if (eventType === 'add_node') {
        const nodeType = data.node_type as string;
        const instanceId = data.instance_id as string | undefined;
        const position = data.position as { x: number; y: number } | undefined;

        const currentGroupedNodes = useNodeStore.getState().groupedNodes;
        const nodeStruct: NodeStruct | null = findCatalogStruct(
          currentGroupedNodes,
          nodeType,
        );

        if (!nodeStruct) {
          console.error(
            `[workflow-chat] Unknown node type "${nodeType}". ` +
              `groupedNodes categories: [${Object.keys(currentGroupedNodes).join(', ')}], ` +
              `total nodes: ${Object.values(currentGroupedNodes).flat().length}`,
          );
          return false;
        }

        saveSnapshot();
        const newNodeId = storeAddNode(
          nodeStruct,
          position ?? { x: 0, y: 200 },
          { selected: false, nodeId: instanceId },
        );
        const configName = data.user_config_name as string | undefined;
        const configDesc = data.user_config_description as string | undefined;
        if (configName && newNodeId) {
          updateNodeAttributes(newNodeId, { user_config_name: configName });
        }
        if (configDesc && newNodeId) {
          updateNodeAttributes(newNodeId, {
            user_config_description: configDesc,
          });
        }
        return true;
      }

      if (eventType === 'remove_node') {
        const instanceId = data.instance_id as string | undefined;
        // onNodesEdgesDelete falls back to deleting the current selection
        // when nodeId is falsy -- an empty/unknown id here must not reach
        // it, or a malformed mutation event could delete whatever the user
        // happens to have selected instead of doing nothing.
        if (
          !instanceId ||
          !useNodeStore.getState().nodes.some((n) => n.id === instanceId)
        ) {
          return false;
        }
        saveSnapshot();
        onNodesEdgesDelete({ nodeId: instanceId });
        return true;
      }

      if (eventType === 'configure_node') {
        const instanceId = data.instance_id as string;
        const updates = data.updates as
          | Array<{ field_id: string; value: string }>
          | undefined;
        if (!updates?.length || !instanceId) {
          return false;
        }
        saveSnapshot();
        for (const { field_id, value } of updates) {
          if (field_id === 'user_config_name') {
            updateNodeAttributes(instanceId, { user_config_name: value });
          } else if (field_id === 'user_config_description') {
            updateNodeAttributes(instanceId, {
              user_config_description: value,
            });
          } else {
            handleParameterChange(
              instanceId,
              field_id,
              coerceChatFieldValue(instanceId, field_id, value),
            );
          }
        }
        return true;
      }

      if (eventType === 'connect_nodes') {
        const sourceId = data.source_instance_id as string;
        const targetId = data.target_instance_id as string;
        const targetParam = data.target_param as string | undefined;

        if (sourceId && targetId) {
          saveSnapshot();
          // Read from the Zustand store directly — getNode() from useReactFlow
          // can miss nodes added earlier in the same synchronous apply batch.
          const storeNodes = useNodeStore.getState().nodes;
          const sourceNode = storeNodes.find((n) => n.id === sourceId);
          const targetNode = storeNodes.find((n) => n.id === targetId);
          if (!sourceNode || !targetNode) {
            return false;
          }

          const sourceStruct = (
            sourceNode.data as unknown as { struct?: Record<string, unknown> }
          )?.struct;
          const targetStruct = (
            targetNode.data as unknown as { struct?: Record<string, unknown> }
          )?.struct;

          const sourceOutputPorts =
            (sourceStruct?.output_ports as Array<{ id: string }>) ?? [];
          const sourceHandleId = sourceOutputPorts[0]?.id ?? '';

          let targetHandleId = '';
          if (targetParam) {
            const params =
              (targetStruct?.params as Array<{
                field_id: string;
                bindable_config?: { input_port?: { id: string } };
              }>) ?? [];
            const param = params.find((p) => p.field_id === targetParam);
            targetHandleId = param?.bindable_config?.input_port?.id ?? '';
            // A specific field was requested but doesn't resolve to a
            // bindable port -- fail rather than silently falling through to
            // the generic port below and wiring the edge to a different
            // field than the agent asked for.
            if (!targetHandleId) {
              return false;
            }
          } else {
            const inputPorts =
              (targetStruct?.input_ports as Array<{ id: string }>) ?? [];
            targetHandleId = inputPorts[0]?.id ?? '';
          }

          if (sourceHandleId && targetHandleId) {
            const connection: Connection = {
              source: sourceId,
              target: targetId,
              sourceHandle: sourceHandleId,
              targetHandle: targetHandleId,
            };
            onConnect(connection);
            return true;
          } else {
            return false;
          }
        }
        return false;
      }

      if (eventType === 'disconnect_nodes') {
        const sourceId = data.source_instance_id as string;
        const targetId = data.target_instance_id as string;
        if (!sourceId || !targetId) {
          return false;
        }
        const edges = useNodeStore.getState().edges;
        const edgesToRemove = edges.filter(
          (e) => e.source === sourceId && e.target === targetId,
        );
        if (edgesToRemove.length === 0) {
          return false;
        }
        saveSnapshot();
        // Not onNodesEdgesDelete: it honours the passed args only when given
        // both nodes and edges, otherwise silently no-ops on the disconnect.
        onEdgesDelete(edgesToRemove as CustomEdge[]);
        return true;
      }

      return false;
    },
    [
      storeAddNode,
      onNodesEdgesDelete,
      onEdgesDelete,
      onConnect,
      handleParameterChange,
      updateNodeAttributes,
      saveSnapshot,
      coerceChatFieldValue,
    ],
  );

  // Apply a batch of buffered workflow mutations onto the canvas: enforces
  // the editing lock, groups the batch (+ auto-layout) into one undo step,
  // and reports success/partial-failure. Returns false when nothing landed
  // -- the batch was empty, blocked by the editing lock, or every mutation
  // in it failed -- so callers know not to mark the proposal as applied and
  // the user can retry.
  const applyBufferedMutations = useCallback(
    (entries: WorkflowMutationEntry[], applyKey?: string): boolean => {
      if (!entries.length) return false;

      // Defense-in-depth — the caller's own Apply button is already disabled
      // while locked, but a proposal minted before the lock engaged could
      // still reach here.
      if (isEditingLockedRef.current) {
        notification.warning(
          getEditingLockedWarning(isWorkflowLockActiveRef.current),
        );
        return false;
      }

      // Only new nodes need arranging. Re-laying out after an edge or field
      // change would clobber a hand-curated canvas.
      const hasNewNodes = entries.some(
        (m) =>
          (m.eventData as { event_type?: string }).event_type === 'add_node',
      );

      let successCount = 0;
      saveSnapshot();
      if (applyKey) useChatApplyStore.getState().forgetApply(applyKey);
      const preApplyState = useNodeStore.getState();
      const beforeSnapshot =
        preApplyState.history[preApplyState.currentHistoryIndex];
      // Keep snapshots suppressed across the async boundary so the
      // mutations + auto-layout are captured as one atomic undo step.
      beginSnapshotSuppression();
      try {
        for (const { eventData } of entries) {
          if (executeWorkflowMutation(eventData)) {
            successCount++;
          }
        }
      } catch (e) {
        endSnapshotSuppression();
        useNodeStore.getState().saveSnapshot();
        throw e;
      }

      const totalCount = entries.length;
      const failedCount = totalCount - successCount;
      const report: ChatApplySaveReport =
        failedCount === 0
          ? {
              summary: `Applied ${totalCount} workflow change${totalCount !== 1 ? 's' : ''}`,
              tone: 'success',
            }
          : {
              summary: `Applied ${successCount} of ${totalCount} changes (${failedCount} failed)`,
              tone: 'warning',
            };
      const notifyReport = () => {
        if (report.tone === 'success') notification.success(report.summary);
        else notification.warning(report.summary);
      };

      // Delay so React can render and measure the new nodes before we
      // compute layout / fit the viewport.
      setTimeout(() => {
        try {
          if (hasNewNodes && isMountedRef.current) {
            const { nodes, edges, setNodes } = useNodeStore.getState();
            if (nodes.length > 1) {
              const positions = getAutoLayoutPositions(nodes, edges, 'LR');
              setNodes(
                nodes.map((node) => {
                  const pos = positions.get(node.id);
                  return pos ? { ...node, position: pos } : node;
                }),
              );
            }
          }
        } finally {
          endSnapshotSuppression();
          useNodeStore.getState().saveSnapshot();
        }
        // The save reports the outcome, so the toast waits for it; with no
        // editor listening (or nothing actually changed) report it directly.
        const { history, currentHistoryIndex } = useNodeStore.getState();
        const afterSnapshot = history[currentHistoryIndex];
        const changed =
          successCount > 0 &&
          !!afterSnapshot &&
          afterSnapshot !== beforeSnapshot;
        if (changed && applyKey && beforeSnapshot && isMountedRef.current) {
          useChatApplyStore
            .getState()
            .recordApply(applyKey, beforeSnapshot, afterSnapshot);
        }
        if (
          changed &&
          isMountedRef.current &&
          useChatApplyStore.getState().saveHandler
        ) {
          requestChatApplySave(report);
        } else {
          notifyReport();
        }
        // Only re-frame the viewport when new nodes were added —
        // fitView would otherwise yank the user's pan/zoom even though
        // nothing on the canvas moved.
        if (hasNewNodes && isMountedRef.current) {
          setTimeout(() => fitView({ padding: 0.15, duration: 300 }), 50);
        }
      }, 100);
      return successCount > 0;
    },
    [executeWorkflowMutation, notification, fitView],
  );

  return { applyBufferedMutations, coerceChatFieldValue };
}
